import type { MediaItem } from '~/types/shop'
import type { ImageTransform } from '~/utils/format/imageUrl'
import { serializeImageUrl } from '~/utils/format/imageUrl'
import type { ImageVariant } from '~/utils/format/imageSizes'
import { IMAGE_VARIANTS } from '~/utils/format/imageSizes'
import type { AppPublicRuntimeConfig } from '~/utils/runtimeConfig'

export const DEFAULT_IMAGE_LIBRARY = 'product-image'

/** Where Sitefinity stores product attribute images, which product cards use as hover images. */
export const PRODUCT_ATTRIBUTE_IMAGE_LIBRARY = 'product-attribute-images'

/** The libraries a product delivery URL can be under, which `getImageKey` reads by default. */
export const PRODUCT_IMAGE_LIBRARIES: readonly string[] = [DEFAULT_IMAGE_LIBRARY, PRODUCT_ATTRIBUTE_IMAGE_LIBRARY]

/** A delivery URL reduced to what the image seam addresses it by. */
export type ImageKey = { library: string, fileName: string }

/**
 * What every delivered product image carries on top of its variant.
 *
 * `quality: 'baseline'` defers to the serializer's delivery baseline rather
 * than pinning a number (ADO 63144), and `format: 'auto'` lets the host
 * negotiate AVIF/WebP.
 *
 * A missing image is a 404, and nothing at delivery substitutes a placeholder
 * (ADO 62991). Rendered images recover client-side: `SImage` swaps in
 * `missingImageUrl`, and plain `<img>` elements wired to `useImageFallback` do
 * the same once their handler is attached. URLs that never reach an element —
 * the PDP's `<link rel="preload">` and `og:image` (`ProductDetailBase`) and the
 * product JSON-LD `image` array (`json-ld.ts`) — are published as-is and 404
 * for a missing image.
 */
const DELIVERY_DEFAULTS: ImageTransform = { quality: 'baseline', format: 'auto' }

/** The variants a template pairs with a `srcset`, whose widest candidate is the variant's own width. */
export type ResponsiveVariant = 'card' | 'pdp-main'

export const getImageUrl = (
  library: string,
  imageName: string | number,
  transform: ImageTransform,
  configuredConfig?: AppPublicRuntimeConfig
): string => serializeImageUrl(library, imageName, transform, configuredConfig)

/**
 * One `srcset` candidate: a width, `scale-down` like every variant, and the
 * delivery defaults. `scale-down` keeps a master smaller than the width from
 * being enlarged; the serializer leaves it out as Cloudflare's default, so a
 * candidate and the variant at the same width are one URL.
 */
const getCandidateUrl = (
  library: string,
  imageName: string | number,
  width: number,
  config?: AppPublicRuntimeConfig
): string => getImageUrl(library, imageName, { width, fit: 'scale-down', ...DELIVERY_DEFAULTS }, config)

export default {
  getFileName (media: string | undefined | null): string {
    if (!media) { return '' }
    const match = media.match(/product-image\/(.+?)(?:\?|$)/)
    if (match && match[1]) {
      return match[1].split('?')[0] ?? ''
    }
    return media
  },

  /**
   * The library and file name of a delivery URL under one of `libraries`, or
   * undefined when it is under none, so the URL cannot be routed through the
   * seam. The query string (Sitefinity's `sfvrsn` version marker) and any
   * fragment are dropped: the delivery URL has no slot for either. Matched as
   * plain text, so a library name is never read as a pattern.
   */
  getImageKey (url: string, libraries: readonly string[] = PRODUCT_IMAGE_LIBRARIES): ImageKey | undefined {
    for (const library of libraries) {
      const segment = `/${library}/`
      const start = url.indexOf(segment)
      if (start === -1) { continue }
      const fileName = url.slice(start + segment.length).split(/[?#]/)[0]
      if (fileName) {
        return { library, fileName }
      }
    }
    return undefined
  },

  getCustomResImage (imageName: string|number, transform: ImageTransform) {
    return getImageUrl(DEFAULT_IMAGE_LIBRARY, imageName, transform)
  },

  /**
   * The `src` for a placement, at the width its variant is governed to.
   *
   * Replaces the single `getLowResImage` preset, which served one 360px
   * transform to slots ranging from a 64px cart row to a 480px category tile —
   * over-serving the small ones and under-serving the large (ADO 62980). The
   * variant is named at the call site so a placement declares its own size
   * rather than inheriting another's.
   */
  getVariantImage (variant: ImageVariant, imageName: string|number, library: string = DEFAULT_IMAGE_LIBRARY, config?: AppPublicRuntimeConfig) {
    return getImageUrl(library, imageName, { ...IMAGE_VARIANTS[variant], ...DELIVERY_DEFAULTS }, config)
  },

  /**
   * The `srcset` candidate list for a responsive placement.
   *
   * Every candidate points at the same master at a different width, so the
   * browser picks one against the `sizes` attribute the template pairs with it.
   * The widths are passed in rather than defaulted: a placement has to say
   * which slot it is filling, since inheriting another's breakpoints is how a
   * srcset silently stops matching its layout.
   *
   * Candidates differ only in width, which keeps the CDN's derivative count to
   * one per breakpoint.
   */
  getSrcSet (imageName: string|number, widths: readonly number[], library: string = DEFAULT_IMAGE_LIBRARY, config?: AppPublicRuntimeConfig) {
    return widths.map(width => `${getCandidateUrl(library, imageName, width, config)} ${width}w`).join(', ')
  },

  /**
   * The `src` for a placement that also carries a `srcset`.
   *
   * Built by the same function as the srcset's candidates, so the fallback is
   * the widest candidate by construction rather than by keeping two recipes in
   * step: a src outside its own srcset is a second cache key and billable
   * transformation for the same pixels (ADO 63948).
   */
  getResponsiveSrc (variant: ResponsiveVariant, imageName: string|number, library: string = DEFAULT_IMAGE_LIBRARY, config?: AppPublicRuntimeConfig) {
    return getCandidateUrl(library, imageName, IMAGE_VARIANTS[variant].width as number, config)
  },

  /**
   * The `src` for a srcset list whose widest width is no variant's — the tablet
   * carousel's tops out at 768. Same reasoning as `getResponsiveSrc`: a src
   * outside its own srcset is a second cache key (ADO 63948).
   */
  getWidestCandidate (imageName: string|number, widths: readonly number[], library: string = DEFAULT_IMAGE_LIBRARY, config?: AppPublicRuntimeConfig) {
    return getCandidateUrl(library, imageName, Math.max(...widths), config)
  },

  // Video utility functions
  isYouTubeId (item: string): boolean {
    // YouTube IDs don't have file extensions
    return !item.includes('.') && item.length > 0
  },

  isImageFile (filename: string): boolean {
    return filename.toLowerCase().endsWith('.jpg') || filename.toLowerCase().endsWith('.jpeg') || filename.toLowerCase().endsWith('.png')
  },

  // Extract YouTube ID from image filename (e.g., "yt_JN19Urj9swYo00.jpg" -> "JN19Urj9swY")
  extractYouTubeIdFromFilename (filename: string): string | null {
    // Match pattern: yt_{youtubeId}o{digits}.jpg where YouTube ID is 11 characters
    const match = filename.match(/^yt_([a-zA-Z0-9_-]{11})o\d{2}\.jpg$/i)
    return match ? (match[1] ?? null) : null
  },

  // Get companion image for YouTube video (e.g., "JN19Urj9swY" -> "yt_JN19Urj9swYo00.jpg")
  getYouTubeCompanionImage (youtubeId: string, images: string[]): string | undefined {
    // Look for image that starts with yt_{YouTube ID} and has oXX suffix
    return images.find((img) => {
      const filename = this.getFileName(img)
      return filename.startsWith(`yt_${youtubeId}`) && /o\d{2}\.jpg$/i.test(filename)
    })
  },

  // Enhanced business logic functions
  separateMediaTypes (mediaArray: string[]): { images: string[], youtubeIds: string[] } {
    const images: string[] = []
    const youtubeIds: string[] = []

    mediaArray.forEach((item) => {
      if (this.isYouTubeId(item)) {
        youtubeIds.push(item)
      } else if (this.isImageFile(item)) {
        images.push(item)

        // Check if this image contains a YouTube ID and extract it
        const filename = this.getFileName(item)
        const extractedYouTubeId = this.extractYouTubeIdFromFilename(filename)
        if (extractedYouTubeId && !youtubeIds.includes(extractedYouTubeId)) {
          youtubeIds.push(extractedYouTubeId)
        }
      }
    })

    return { images, youtubeIds }
  },

  extractFilenames (mediaArray: string[]): string[] {
    return mediaArray.map((item) => {
      if (item.includes('/')) {
        return this.getFileName(item)
      }
      return item
    })
  },

  getImageVideoPairs (images: string[], youtubeIds: string[]): MediaItem[] {
    return images.map((image) => {
      // Check if this image is a YouTube companion (e.g., "yt_JN19Urj9swYo00.jpg")
      const youtubeId = youtubeIds.find(ytId => image.startsWith(`yt_${ytId}`))

      return {
        image,
        youtubeId,
        thumbnailSrc: this.getVariantImage('thumbnail', image)
      }
    })
  },

  /**
   * The image the PDP gallery shows first, as a filename.
   *
   * The gallery sorts by oXX suffix (`createPairedMedia`), so the API's first
   * entry is not necessarily it. The main-image preload and og:image have to
   * name the same image, or the preload fetches a main-size image nothing
   * paints while the one shown loads without it (ADO 63948).
   */
  getPrimaryImage (media: string[]): string | undefined {
    const filenames = this.extractFilenames(this.separateMediaTypes(media).images)
    return [...filenames].sort((a, b) => this.getMediaPosition(a) - this.getMediaPosition(b))[0]
  },

  // Extract position from oXX suffix for sorting
  // Returns: -1 for no suffix (comes first), then 0, 1, 2, etc. for o00, o01, o02
  getMediaPosition (filename: string): number {
    // Match oXX pattern where XX is digits
    const match = filename.match(/o(\d{2})\.jpg$/i)
    if (match && match[1]) {
      return parseInt(match[1], 10)
    }
    // Files without oXX suffix (e.g., "166229.jpg") come first, use -1 to sort before o00
    return -1
  },

  // Enhanced paired media creation with filename extraction and sorting
  createPairedMedia (images: string[], youtubeIds: string[]): MediaItem[] {
    const imageFilenames = this.extractFilenames(images)

    const pairs = this.getImageVideoPairs(imageFilenames, youtubeIds)

    if (!Array.isArray(pairs) || pairs == null) {
      return []
    }

    // Sort by position extracted from oXX suffix
    return pairs.filter(Boolean).sort((a, b) => {
      const posA = this.getMediaPosition(a.image)
      const posB = this.getMediaPosition(b.image)
      return posA - posB
    })
  },

  // Create MediaItem with proper thumbnail URL
  createMediaItem (image: string, library: string = DEFAULT_IMAGE_LIBRARY, youtubeId?: string): MediaItem {
    return {
      image,
      youtubeId,
      thumbnailSrc: this.getVariantImage('thumbnail', image, library)
    }
  }
}
